/** Session flag: "1" = creator onboarding (social → Stripe → profile), "0" = fan. Set after invite verify on /signup; cleared when appropriate. */
export const SIGNUP_CREATOR_SESSION_KEY = "hyk_signup_is_creator";

/**
 * Fallback when `/api/invitations/verify` does not yet return `username` / `is_creator`.
 * Written when the soft-wall invite is sent; matched by email after verify.
 *
 * Preferred long-term: store these on the **invitation** (pending signup) record and return them from verify — no user row required until signup completes.
 */
export const PENDING_SOFTWALL_INVITE_STORAGE_KEY = "hyk_pending_softwall_invite";
export const PENDING_CONTRIBUTION_INTENT_KEY = "hyk_pending_contribution_intent";
export const VERIFY_EMAIL_BANNER_EMAIL_KEY = "hyk_verify_email_banner_email";
export const PASSWORD_SETUP_PENDING_KEY_PREFIX = "hyk_password_setup_pending";

export function getPasswordSetupPendingKey(email) {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail) {
        return `${PASSWORD_SETUP_PENDING_KEY_PREFIX}:anonymous`;
    }
    return `${PASSWORD_SETUP_PENDING_KEY_PREFIX}:${normalizedEmail}`;
}

export function markPasswordSetupPending(email) {
    try {
        localStorage.setItem(getPasswordSetupPendingKey(email), "1");
    } catch {
        // ignore
    }
}

export function clearPasswordSetupPending(email) {
    try {
        localStorage.removeItem(getPasswordSetupPendingKey(email));
    } catch {
        // ignore
    }
}

export function isPasswordSetupPending(email) {
    try {
        return localStorage.getItem(getPasswordSetupPendingKey(email)) === "1";
    } catch {
        return false;
    }
}

/**
 * Server sets Supabase Auth `app_metadata.requires_password_setup` for softwall (temp password) users.
 * Prefer that over localStorage so banners stay correct across devices.
 * @param {{ user?: { app_metadata?: Record<string, unknown>, email?: string } } | null} session
 */
export function requiresPasswordSetupFromSession(session) {
    const flag = session?.user?.app_metadata?.requires_password_setup;
    if (flag === false) return false;
    if (flag === true) return true;
    const email = session?.user?.email;
    return email ? isPasswordSetupPending(email) : false;
}

/**
 * Same rule as Explore’s verify-email banner: signed-in user must verify email and finish password setup.
 * @param {{ user_id?: string } | null} user
 * @param {{ user?: { email_confirmed_at?: string | null, confirmed_at?: string | null, email?: string, app_metadata?: Record<string, unknown> } } | null} session
 */
export function userNeedsEmailVerificationOrPasswordSetup(user, session) {
    if (!user?.user_id) return false;
    const confirmedAt = session?.user?.email_confirmed_at || session?.user?.confirmed_at;
    const isEmailVerified = !!confirmedAt;
    const passwordSetupPending = requiresPasswordSetupFromSession(session);
    return !isEmailVerified || passwordSetupPending;
}

/**
 * @param {{ email: string, username: string, is_creator: boolean }} payload
 */
export function savePendingSoftwallInvite({ email, username, is_creator }) {
    if (!email?.trim() || !username?.trim()) return;
    try {
        const payload = {
            email: email.trim().toLowerCase(),
            username: username.trim().toUpperCase(),
            is_creator: !!is_creator,
            savedAt: Date.now(),
        };
        localStorage.setItem(PENDING_SOFTWALL_INVITE_STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // quota / private mode
    }
}

/**
 * Read pending soft-wall signup for an email (does not remove — survives refresh until signup completes).
 * @param {string} emailFromApi
 * @returns {{ email: string, username: string, is_creator: boolean, savedAt: number } | null}
 */
export function peekPendingSoftwallInvite(emailFromApi) {
    if (!emailFromApi?.trim()) return null;
    try {
        const raw = localStorage.getItem(PENDING_SOFTWALL_INVITE_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.email !== emailFromApi.trim().toLowerCase()) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function clearPendingSoftwallInvite() {
    try {
        localStorage.removeItem(PENDING_SOFTWALL_INVITE_STORAGE_KEY);
    } catch {
        // ignore
    }
}

/** Best-effort read of username from verify payload (snake_case / nested). */
export function pickInviteUsernameFromData(data) {
    if (!data) return null;
    const candidates = [
        data.userName,
        data.username,
        data.user_name,
        data.invited_username,
        data.reserved_username,
        data.pending_username,
        data.invitation?.username,
        data.invitation?.userName,
        data.invitation?.user_name,
        data.invitation?.reserved_username,
        data.user?.userName,
        data.user?.username,
    ];
    for (const raw of candidates) {
        if (raw != null && String(raw).trim() !== "") {
            return String(raw).trim();
        }
    }
    return null;
}

/**
 * @param {object | null} data — verify API body
 * @param {URLSearchParams} searchParams
 */
export function resolveUsernameFromInvite(data, searchParams) {
    const fromApi = pickInviteUsernameFromData(data);
    if (fromApi) return fromApi;
    const fromUrl =
        searchParams.get("username") ||
        searchParams.get("user_name") ||
        searchParams.get("user");
    if (fromUrl?.trim()) return fromUrl.trim();
    const email = data?.email;
    if (email) {
        const pending = peekPendingSoftwallInvite(email);
        if (pending?.username) return pending.username;
    }
    return null;
}

/**
 * Creator flag: API / URL first; if absent, soft-wall localStorage pending invite.
 * @param {object | null} data
 * @param {URLSearchParams} searchParams
 */
export function resolveCreatorFromInvite(data, searchParams) {
    if (searchParams.get("creator") === "1") return true;
    if (!data) return false;
    if (data.is_creator === true || data.isCreator === true) return true;
    if (data.role === "creator" || data.signup_role === "creator") return true;
    if (data.is_creator === false || data.isCreator === false) return false;
    if (data.role === "fan" || data.signup_role === "fan") return false;
    const pending = data.email ? peekPendingSoftwallInvite(data.email) : null;
    if (pending) return !!pending.is_creator;
    return false;
}

export function savePendingContributionIntent(intent) {
    try {
        localStorage.setItem(PENDING_CONTRIBUTION_INTENT_KEY, JSON.stringify(intent));
    } catch {
        // ignore storage errors
    }
}

export function readPendingContributionIntent() {
    try {
        const raw = localStorage.getItem(PENDING_CONTRIBUTION_INTENT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export function clearPendingContributionIntent() {
    try {
        localStorage.removeItem(PENDING_CONTRIBUTION_INTENT_KEY);
    } catch {
        // ignore
    }
}
